/**
 * Post-registration onboarding page.
 *
 * Shown when an authenticated user has no organizations.
 * This happens when a user registers but hasn't been added to any org yet.
 *
 * Two contexts:
 *  - Came from an app (return_to or source_app in URL) → show app-specific guidance
 *  - Self-directed (no return_to) → show generic "check email" guidance
 *
 * Note: self-service org creation is not offered here because the backend
 * endpoint is admin-only. The org manager flow handles org creation.
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

const BASE_DOMAIN = config.baseDomain;
const PASSPORT_HOST = `passport.${BASE_DOMAIN}`;

function getSourceApp(search: string, returnTo: string): string | undefined {
  const params = new URLSearchParams(search);
  const explicit = params.get('source_app');
  if (explicit) return explicit;

  try {
    const url = new URL(returnTo);
    const host = url.hostname;
    if (host.endsWith(`.${BASE_DOMAIN}`) && host !== PASSPORT_HOST) {
      const sub = host.replace(`.${BASE_DOMAIN}`, '');
      return sub.charAt(0).toUpperCase() + sub.slice(1);
    }
    if (host !== PASSPORT_HOST && host !== 'localhost') {
      return host;
    }
  } catch {
    // Not a valid URL
  }
  return undefined;
}

export default function Onboarding() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, organizations } = useAuth();

  const params = new URLSearchParams(location.search);
  const returnTo = params.get('return_to') ?? '';
  const sourceApp = getSourceApp(location.search, returnTo);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    // If user somehow has orgs, send them to settings
    if (organizations.length > 0) {
      navigate('/settings', { replace: true });
    }
  }, [isLoading, isAuthenticated, organizations, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Account created</h1>
          <p className="text-gray-500 mt-2">
            {sourceApp
              ? `To access ${sourceApp}, you need to be part of an organization.`
              : `You're not part of any organization yet.`}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          <div className="p-5">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-teal-50 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Check your email</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  You may have received an invitation. Click the link in that email to join an organization.
                </p>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-teal-50 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {sourceApp ? `Contact your ${sourceApp} administrator` : 'Contact your organization administrator'}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Ask them to invite you to their organization. They can do this from the Org Management page.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/settings')}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            Go to settings
          </button>
        </div>
      </div>
    </div>
  );
}
